import { Location } from "@angular/common";
import { inject, Injectable } from "@angular/core";

import { toSignal } from "@angular/core/rxjs-interop";
import {
    ActivatedRoute,
    ActivatedRouteSnapshot,
    NavigationCancel,
    NavigationEnd,
    NavigationError,
    NavigationStart,
    Params,
    Router,
} from "@angular/router";

import { distinctUntilChanged, filter, map } from "rxjs";

import {
    FILTERS_QUERY_PARAM,
    SEARCH_QUERY_PARAM,
    SORTBY_QUERY_PARAM,
} from "@hermes/hermes-web-fragments-filters-utils";

import { BackToGridService } from "@hermes/hermes-web-fragments-product-utils";
import { LocaleUrlPrefix } from "@hermes/locale";
import {
    FeatureFlagFacade,
    GRID_REMOVE_URL_FRAGMENT,
} from "@hermes/hermes-web-states-flipper-state";

import { getFacetsQueryParameters } from "../../helpers/criterias/criterias.helper";
import { DEFAULT_SORT, Facet } from "../../store/grid.store.model";

/**
 * Represents data stored in URL
 */
export interface GridUrlData {
    facet: Facet;
    fhParams: string | undefined;
    sort: string | undefined;
    searchTerm?: string;
}

/**
 * A service who manage URL hash because we have to save some data into URL :
 * - fhParams corresponding to the FH params for the current filtered grid
 * - sort corresponding to the current sort applied
 * - open filters corresponding to the current filter groups opened
 */
@Injectable({
    providedIn: "root",
})
export class UrlManagerService {
    private location = inject(Location);
    private backToGridService = inject(BackToGridService);
    private route = inject(ActivatedRoute);
    private router = inject(Router);

    /**
     * Replace all categories filters fragments by queryParam.
     * @private
     */
    private readonly gridRemoveUrlFragment = toSignal(
        inject(FeatureFlagFacade).isActivated(GRID_REMOVE_URL_FRAGMENT),
    );

    /**
     * Build grid page URL with given datas
     * @returns a grid page URL
     */
    public buildUrl(urlData: GridUrlData): string {
        const tree = this.router.parseUrl(this.router.url);
        const currentQueryParameters = { ...tree.queryParams };

        // We exclude FH, sortby and search query params to be properly rebuilt later.
        const queryParametersToDelete = new Set([
            FILTERS_QUERY_PARAM,
            SORTBY_QUERY_PARAM,
            SEARCH_QUERY_PARAM,
        ]);

        for (const key of Object.keys(currentQueryParameters)) {
            // We also exclude facet SEO to rebuild properly later.
            if (key.startsWith("facet_") || queryParametersToDelete.has(key)) {
                delete currentQueryParameters[key];
            }
        }

        if (urlData.searchTerm) {
            currentQueryParameters[SEARCH_QUERY_PARAM] = urlData.searchTerm;
        }

        // We replace fragment usage by new FH query param.
        if (
            this.gridRemoveUrlFragment() &&
            !urlData.facet.enabled &&
            urlData.fhParams
        ) {
            currentQueryParameters[FILTERS_QUERY_PARAM] = urlData.fhParams;
        }

        // Restore the facet query params.
        for (const [k, v] of getFacetsQueryParameters(
            urlData.facet.combinedFacets,
        )) {
            currentQueryParameters[k] = String(v);
        }

        // We replace fragment usage by new sort query param.
        const sortParameter = this.getSortParameter(urlData.sort);
        if (this.gridRemoveUrlFragment() && sortParameter) {
            currentQueryParameters[SORTBY_QUERY_PARAM] = sortParameter;
        }

        const fragment = this.buildFragment(urlData);

        const newTree = this.router.createUrlTree([], {
            relativeTo: this.route,
            queryParams: currentQueryParameters,
            fragment,
        });

        return this.router.serializeUrl(newTree);
    }

    /**
     * Update url fragment with given url datas
     */
    public updateUrl(urlData: GridUrlData): void {
        this.location.replaceState(this.buildUrl(urlData));
    }

    /**
     * Save current grid URL to allow back to grid.
     */
    public saveCurrentGrid(): void {
        const newUrl = this.location.path(true);
        // Save URL into session storage for back to grid.
        this.backToGridService.storeGridUrl(newUrl);
    }

    public resetCurrentGrid(): void {
        this.backToGridService.cleanGridUrl();
    }

    /**
     * This method creates a fragment as string based on given criteria.
     * The fragment is a two parts string  as [ urlParams | sort ]
     */
    public buildFragment({
        facet,
        fhParams,
        sort,
    }: GridUrlData): string | undefined {
        // No more fragments for filters.
        // A fragment for productSku will still be applied later.
        if (this.gridRemoveUrlFragment()) {
            return undefined;
        }

        if ((fhParams && fhParams.length > 0) || (sort && sort?.length > 0)) {
            return [
                // first fragment url params from merchandising
                facet?.enabled ? "" : fhParams,

                // second fragment sort
                sort && sort !== DEFAULT_SORT ? sort : undefined,
            ].join("|");
        }

        return undefined;
    }

    public parseRoute(route: ActivatedRouteSnapshot): GridUrlData {
        return {
            ...this.parseFragment(route.fragment || ""),
            ...this.parseQueryParams(route.queryParams || {}),
        };
    }

    /**
     * Remove all query parameters to keep baseUrl.
     */
    public getUrlWithoutParams(localUrlPrefix: LocaleUrlPrefix) {
        const urlTree = this.router.parseUrl(this.location.path());
        urlTree.queryParams = {};
        const containsTrailingSlash = urlTree.toString().charAt(0) === "/";

        return `${localUrlPrefix}${
            containsTrailingSlash ? "" : "/"
        }${urlTree.toString()}`;
    }

    private parseQueryParams(
        queryParameters: Params,
    ): Pick<GridUrlData, "facet"> {
        const combinedFacets = Object.entries(queryParameters)
            .filter(([key]) => key.startsWith("facet_"))
            .map(([facetName, facetNameValue]) => ({
                facetName,
                facetNameValue,
            }));

        const facetParameters = Object.keys(queryParameters).find((key) =>
            key.startsWith("facet_"),
        );

        const facet = {
            facetName: facetParameters || "",
            facetNameValue: facetParameters
                ? queryParameters[facetParameters]
                : "",
            combinedFacets: facetParameters ? combinedFacets : [],
            facetDisplayName: "",
            facetTitle: "",
            updateMetas: !!facetParameters,
            enabled: !!facetParameters,
            path: "",
        };

        if (!this.gridRemoveUrlFragment()) {
            return { facet };
        }

        // We can pass filters params by a blacklisted query param to avoid fragment in url.
        const gridParameters = queryParameters[FILTERS_QUERY_PARAM];
        // Sort param is no more on fragment second position, we use a query param.
        const sortParameter = this.getSortParameter(
            queryParameters[SORTBY_QUERY_PARAM],
        );
        return {
            ...(gridParameters && { fhParams: gridParameters }),
            ...(sortParameter && { sort: sortParameter }),
            facet,
        };
    }

    /**
     * Retrieve some data from fragment in grid URL.
     *
     * Grid fragment is a two parts string following this pattern `"urlParams | sort"`
     */
    private parseFragment(
        fragment: string,
    ): Pick<GridUrlData, "fhParams" | "sort"> {
        // Since fragment removal, we only keep it to allow retro compatibility.
        // TODO simplify at flag GRID_REMOVE_URL_FRAGMENT removal.
        const [fhParameters, sort] = fragment.split("|");

        return {
            fhParams: fhParameters,
            sort,
        };
    }

    private getSortParameter(sort?: string): string | undefined {
        return sort === DEFAULT_SORT ? "" : sort;
    }

    public readonly isNavigated = toSignal(
        this.router.events.pipe(
            filter(
                (event) =>
                    event instanceof NavigationStart ||
                    event instanceof NavigationCancel ||
                    event instanceof NavigationError ||
                    event instanceof NavigationEnd,
            ),
            map((event) => !(event instanceof NavigationStart)),
            distinctUntilChanged(),
        ),
        { initialValue: false },
    );
}
